import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../services/order';
import { tableService } from '../services/table';
import { customerService } from '../services/customer';
import { itemService } from '../services/item';
import { printService, willUseShare } from '../services/print';
import { printConfigurationService } from '../services/printConfiguration';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { Order, PaymentMode } from '../types';
import {
  ShoppingCart,
  Plus,
  Search,
  IndianRupee,
  Users,
  Calendar,
  Receipt,
  CreditCard,
  Banknote,
  X,
  Filter,
  Eye,
  Clock3,
  CheckCircle,
  Phone,
  Edit3,
  Minus,
  Save,
  Percent,
  Printer,
  Share2
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { EmptyState, ButtonLoader, SkeletonList, SkeletonCard } from '../components/LoadingStates';

interface EditOrderData {
  notes: string;
  discountPercent: number;
  discountAmount: number;
  items: Array<{
    itemId: string;
    name: string;
    quantity: number;
    unitPrice: number;
    specialNotes: string;
  }>;
}

export const Orders: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [debouncedCustomerFilter, setDebouncedCustomerFilter] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editOrderData, setEditOrderData] = useState<EditOrderData | null>(null);
  const [settlementData, setSettlementData] = useState({
    paymentMode: 'CASH' as PaymentMode,
    customerId: '',
    customerName: '',
    customerMobile: '',
    customerEmail: '',
    customerAddress: '',
    discountPercent: null as number | null,
    discountAmount: null as number | null
  });
  const [discountType, setDiscountType] = useState<'percent' | 'amount' | null>(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [debouncedCustomerSearchTerm, setDebouncedCustomerSearchTerm] = useState('');
  const [showCustomerSearchDropdown, setShowCustomerSearchDropdown] = useState(false);
  const [selectedCustomerFromSearch, setSelectedCustomerFromSearch] = useState<any>(null);
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [debouncedItemSearchTerm, setDebouncedItemSearchTerm] = useState('');
  const [printingOrderId, setPrintingOrderId] = useState<string | null>(null);

  const activeRestaurantId = getActiveRestaurantId();

  // Determine which API to use for fetching orders
  const getOrdersQueryKey = () => {
    if (selectedCustomer) {
      return ['orders', 'by-customer', user?.organizationCode, selectedCustomer.id];
    } else if (dateRange.start && dateRange.end) {
      return ['orders', 'by-date', user?.organizationCode, dateRange.start, dateRange.end];
    } else {
      return ['orders', 'open', user?.organizationCode, activeRestaurantId];
    }
  };

  // Fetch orders based on filters
  const { data: ordersResponse, isLoading: ordersLoading } = useQuery(
    getOrdersQueryKey(),
    async () => {
      if (selectedCustomer) {
        // Use by-customer API
        return orderService.getOrdersByCustomer(user?.organizationCode!, selectedCustomer.id);
      } else if (dateRange.start && dateRange.end) {
        // Use by-date API with ISO format
        const startISO = new Date(dateRange.start + 'T00:00:00.000Z').toISOString();
        const endISO = new Date(dateRange.end + 'T23:59:59.999Z').toISOString();
        return orderService.getOrdersByDateRange(user?.organizationCode!, startISO, endISO);
      } else {
        // Default: fetch open orders only
        return orderService.getOpenOrders(user?.organizationCode!, activeRestaurantId);
      }
    },
    { enabled: !!user?.organizationCode }
  );

  // Extract orders from response (handle both paginated and direct array responses)
  const orders = ordersResponse?.content || ordersResponse || [];

  // Fetch all tables to get table names
  const { data: allTables } = useQuery(
    ['all-tables', user?.organizationCode],
    () => tableService.getTables({ org: user?.organizationCode }),
    { enabled: !!user?.organizationCode }
  );

  // Fetch BILL print config to check if GST is applicable
  const { data: printConfigs } = useQuery(
    ['printConfigs', user?.organizationCode, activeRestaurantId],
    () => printConfigurationService.getAll(user?.organizationCode!, activeRestaurantId),
    { enabled: !!user?.organizationCode }
  );
  const billConfig = printConfigs?.find(c => c.printType === 'BILL');
  const isGstApplicable = billConfig?.gstApplicable !== false;

  // Fetch all items or search based on term for edit order modal
  const { data: searchItems, isLoading: searchItemsLoading } = useQuery(
    ['search-items', user?.organizationCode, debouncedItemSearchTerm],
    async () => {
      if (debouncedItemSearchTerm.trim()) {
        return itemService.searchItems(user?.organizationCode!, debouncedItemSearchTerm.trim());
      } else {
        // Fetch all items when no search term
        return itemService.getItems({ org: user?.organizationCode! });
      }
    },
    {
      enabled: !!user?.organizationCode && !!showEditModal,
      keepPreviousData: true
    }
  );

  // Debounce customer filter changes (1.5 second delay like Customers page)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerFilter(customerFilter);
    }, 1500);

    return () => clearTimeout(timer);
  }, [customerFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCustomerDropdown) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCustomerDropdown]);

  // Fetch customers for search using same approach as Customers page
  const { data: customers } = useQuery(
    ['customers-search', user?.organizationCode, debouncedCustomerFilter],
    async () => {
      if (debouncedCustomerFilter.trim()) {
        // Use search API for name/mobile search (same as Customers page)
        return customerService.search(user?.organizationCode!, debouncedCustomerFilter.trim(), 100);
      } else {
        // Use getCustomers for initial load or when no search term
        return customerService.getCustomers({ org: user?.organizationCode, restaurantId: activeRestaurantId });
      }
    },
    {
      enabled: !!user?.organizationCode,
      keepPreviousData: true
    }
  );

  // Debounce customer search term for settlement modal
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerSearchTerm(customerSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [customerSearchTerm]);

  // Debounce item search term for edit order modal
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedItemSearchTerm(itemSearchTerm);
    }, 1500);
    return () => clearTimeout(timer);
  }, [itemSearchTerm]);

  // Customer search for settlement modal
  const { data: customerSearchResults } = useQuery(
    ['customer-search-settlement', user?.organizationCode, debouncedCustomerSearchTerm],
    async () => {
      if (debouncedCustomerSearchTerm.trim() && debouncedCustomerSearchTerm.length >= 2) {
        return customerService.search(user?.organizationCode!, debouncedCustomerSearchTerm.trim(), 10);
      }
      return [];
    },
    {
      enabled: !!user?.organizationCode && !!debouncedCustomerSearchTerm && debouncedCustomerSearchTerm.length >= 2,
      keepPreviousData: true
    }
  );

  // Create table mapping for quick lookup
  const tableMap = useMemo(() => {
    if (!allTables || !Array.isArray(allTables)) return {};
    return allTables.reduce((acc, t) => {
      acc[String(t.id)] = t.name ?? `Table ${t.id}`;
      return acc;
    }, {} as Record<string, string>);
  }, [allTables]);


  const getTableName = (id?: string | number) => {
    console.log("ID ", id)
    if (!id) return '—';
    return tableMap[String(id)] ?? '—';
  };


  // Create customer mapping
  const customerMap = useMemo(() => {
    if (!customers || !Array.isArray(customers)) return {};
    return customers.reduce((acc, customer) => {
      acc[customer.id] = customer;
      return acc;
    }, {} as Record<string, any>);
  }, [customers]);

  // Settle order mutation
  const settleOrderMutation = useMutation(
    (settlementData: any) => orderService.settleOrder(settlementData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['tables']);
        setShowSettleModal(false);
        setSelectedOrder(null);
        toast.success('Order settled successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to settle order');
      }
    }
  );

  // Settle order and print mutation
  const settleOrderAndPrintMutation = useMutation(
    (settlementData: any) => orderService.settleOrder(settlementData),
    {
      onSuccess: async (order: Order) => {
        toast.success('Order settled successfully!');

        // Print/Share Bill
        try {
          await printService.printBill(order.id, user?.organizationCode!, true);
          toast.success(willUseShare() ? 'Bill shared successfully!' : 'Bill sent to print!');
        } catch (error) {
          console.error('Failed to print/share bill:', error);
          toast.error(willUseShare() ? 'Order settled but sharing failed' : 'Order settled but printing failed');
        }

        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['tables']);
        setShowSettleModal(false);
        setSelectedOrder(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to settle order');
      }
    }
  );

  // Update order mutation
  const updateOrderMutation = useMutation(
    ({ orderId, updateData }: { orderId: string; updateData: any }) =>
      orderService.updateOrder(orderId, updateData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['orders']);
        setShowEditModal(false);
        setSelectedOrder(null);
        setEditOrderData(null);
        toast.success('Order updated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update order');
      }
    }
  );

  // Filter orders based on search criteria (only order ID and table name search)
  const filteredOrders = (orders && Array.isArray(orders) ? orders.filter(order => {
    const matchesSearch = searchTerm === '' ||
      (order.orderNo?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tableMap[order.tableId]?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  }) : []);

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleEditOrder = (order: Order) => {
    setSelectedOrder(order);
    setEditOrderData({
      notes: order.notes || '',
      discountPercent: order.discountPercent || 0,
      discountAmount: order.discountAmount || 0,
      items: order.items.map((item: any) => ({
        itemId: item.itemId,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        specialNotes: item.specialNotes || ''
      }))
    });
    setItemSearchTerm(''); // Clear search term when opening modal
    setShowEditModal(true);
  };

  const handlePrint = async (order: Order) => {
    setPrintingOrderId(order.id);
    try {
      const useShare = willUseShare();
      if (order.status === 'SETTLED') {
        await printService.printBill(order.id, user?.organizationCode!, true);
        toast.success(useShare ? 'Bill shared successfully!' : 'Bill sent to print!');
      } else {
        await printService.printKOT(order.id, user?.organizationCode!, true);
        toast.success(useShare ? 'KOT shared successfully!' : 'KOT sent to print!');
      }
    } catch (error) {
      console.error('Failed to print/share:', error);
      toast.error(willUseShare() ? 'Failed to share document' : 'Failed to print document');
    } finally {
      setPrintingOrderId(null);
    }
  };

  const addItemToEdit = (item: any) => {
    if (!editOrderData) return;

    const existingItemIndex = editOrderData.items.findIndex((i) => i.itemId === item.id);

    if (existingItemIndex >= 0) {
      // Increase quantity if item already exists
      const updatedItems = [...editOrderData.items];
      updatedItems[existingItemIndex].quantity += 1;
      setEditOrderData((prev: EditOrderData | null) => prev ? { ...prev, items: updatedItems } : null);
    } else {
      // Add new item
      setEditOrderData((prev: EditOrderData | null) => prev ? {
        ...prev,
        items: [...prev.items, {
          itemId: item.id,
          name: item.name,
          quantity: 1,
          unitPrice: item.price,
          specialNotes: ''
        }]
      } : null);
    }
  };

  const updateItemQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      // Remove item if quantity is 0 or less
      setEditOrderData((prev: EditOrderData | null) => prev ? {
        ...prev,
        items: prev.items.filter((i) => i.itemId !== itemId)
      } : null);
    } else {
      // Update quantity
      setEditOrderData((prev: EditOrderData | null) => prev ? {
        ...prev,
        items: prev.items.map((i) =>
          i.itemId === itemId ? { ...i, quantity: newQuantity } : i
        )
      } : null);
    }
  };

  const updateItemNotes = (itemId: string, notes: string) => {
    setEditOrderData((prev: EditOrderData | null) => prev ? {
      ...prev,
      items: prev.items.map((i) =>
        i.itemId === itemId ? { ...i, specialNotes: notes } : i
      )
    } : null);
  };

  const confirmOrderUpdate = () => {
    if (!selectedOrder || !editOrderData) return;

    const updatePayload = {
      notes: editOrderData.notes,
      discountPercent: editOrderData.discountPercent > 0 ? editOrderData.discountPercent : undefined,
      discountAmount: editOrderData.discountAmount > 0 ? editOrderData.discountAmount : undefined,
      items: editOrderData.items.map((item) => ({
        itemId: item.itemId,
        quantity: item.quantity,
        specialNotes: item.specialNotes || undefined
      }))
    };

    updateOrderMutation.mutate({
      orderId: selectedOrder.id,
      updateData: updatePayload
    });
  };

  // Email validation
  const validateEmail = (email: string) => {
    if (!email.trim()) return '';
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? '' : 'Please enter a valid email address';
  };

  // Mobile number validation
  const validateMobile = (mobile: string) => {
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobile) return '';
    if (!mobileRegex.test(mobile)) {
      return 'Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9';
    }
    return '';
  };

  // Handle mobile number input with validation and search
  const handleMobileChange = (value: string) => {
    // Only allow digits and limit to 10 characters
    const numericValue = value.replace(/\D/g, '').slice(0, 10);

    setSettlementData(prev => ({ ...prev, customerMobile: numericValue }));
    setMobileError(validateMobile(numericValue));

    // Use mobile for customer search
    setCustomerSearchTerm(numericValue);
    if (numericValue.length >= 2) {
      setShowCustomerSearchDropdown(true);
    } else {
      setShowCustomerSearchDropdown(false);
      setSelectedCustomerFromSearch(null);
    }
  };

  // Handle customer selection from search results
  const handleCustomerSelectFromSearch = (customer: any) => {
    setSelectedCustomerFromSearch(customer);
    setSettlementData(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.name,
      customerMobile: customer.mobile,
      customerEmail: customer.email || '',
      customerAddress: customer.address || ''
    }));
    setCustomerSearchTerm(customer.mobile);
    setShowCustomerSearchDropdown(false);
    setMobileError('');
  };

  // Clear customer selection and use manual entry
  const handleClearCustomerSelection = () => {
    setSelectedCustomerFromSearch(null);
    setSettlementData(prev => ({
      ...prev,
      customerId: '',
      customerName: '',
      customerEmail: '',
      customerAddress: ''
    }));
  };

  const handleSettle = (order: Order, customer?: any) => {
    setSelectedOrder(order);
    setSettlementData({
      paymentMode: 'CASH',
      customerId: customer?.id || '',
      customerName: customer?.name || '',
      customerMobile: customer?.mobile || '',
      customerEmail: customer?.email || '',
      customerAddress: customer?.customerAddress || '',
      discountPercent: null,
      discountAmount: null
    });
    setDiscountType(null);
    setCustomerSearchTerm('');
    setSelectedCustomerFromSearch(null);
    setMobileError('');
    setEmailError('');
    setShowCustomerSearchDropdown(false);
    setShowSettleModal(true);
  };

  const handleCustomerSelect = (customer: any) => {
    setSelectedCustomer(customer);
    setCustomerFilter(customer.name + ' - ' + customer.mobile);
    setShowCustomerDropdown(false);
    setDebouncedCustomerFilter(''); // Clear the search
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setCustomerFilter('');
    setDebouncedCustomerFilter('');
    setSelectedCustomer(null);
    setDateRange({ start: '', end: '' });
    setShowCustomerDropdown(false);
  };

  const confirmSettlement = () => {
    if (!selectedOrder) return;

    if (settlementData.customerMobile) {
      const mobileValidationError = validateMobile(settlementData.customerMobile);
      if (mobileValidationError) {
        toast.error(mobileValidationError);
        return;
      }
    }

    if (settlementData.customerEmail) {
      const emailValidationError = validateEmail(settlementData.customerEmail);
      if (emailValidationError) {
        setEmailError(emailValidationError);
        toast.error(emailValidationError);
        return;
      }
    }

    // For DUE payments, additional validation is already covered above
    if (settlementData.paymentMode === 'DUE') {
      // Always validate mobile and name (required for all payment modes)
      if (!settlementData.customerMobile.trim()) {
        toast.error('Mobile number is required');
        return;
      }

      if (!settlementData.customerName.trim()) {
        toast.error('Customer name is required');
        return;
      }

      // Validate mobile number format
      const mobileValidationError = validateMobile(settlementData.customerMobile);
      if (mobileValidationError) {
        toast.error(mobileValidationError);
        return;
      }
    }

    // Prepare settlement data with customer information and discounts
    const settlementPayload = {
      orderId: selectedOrder.id,
      paymentMode: settlementData.paymentMode,
      customerId: settlementData.customerId || undefined, // Only include if customer was selected from search
      customerName: settlementData.customerName,
      customerMobile: settlementData.customerMobile,
      customerEmail: settlementData.customerEmail || undefined,
      customerAddress: settlementData.customerAddress || undefined,
      // Include discount - only one type at a time
      discountPercent: (settlementData.discountPercent && settlementData.discountPercent > 0) ? settlementData.discountPercent : undefined,
      discountAmount: (settlementData.discountAmount && settlementData.discountAmount > 0) ? settlementData.discountAmount : undefined
    };

    // Filter out undefined values to keep payload clean
    const cleanPayload = Object.fromEntries(
      Object.entries(settlementPayload).filter(([_, value]) => value !== undefined && value !== '')
    );

    settleOrderMutation.mutate(cleanPayload);
  };

  const confirmSettlementAndPrint = () => {
    if (!selectedOrder) return;

    if (settlementData.customerMobile) {
      const mobileValidationError = validateMobile(settlementData.customerMobile);
      if (mobileValidationError) {
        toast.error(mobileValidationError);
        return;
      }
    }

    if (settlementData.customerEmail) {
      const emailValidationError = validateEmail(settlementData.customerEmail);
      if (emailValidationError) {
        setEmailError(emailValidationError);
        toast.error(emailValidationError);
        return;
      }
    }

    // For DUE payments, additional validation is already covered above
    if (settlementData.paymentMode === 'DUE') {
      // Always validate mobile and name (required for all payment modes)
      if (!settlementData.customerMobile.trim()) {
        toast.error('Mobile number is required');
        return;
      }

      if (!settlementData.customerName.trim()) {
        toast.error('Customer name is required');
        return;
      }

      // Validate mobile number format
      const mobileValidationError = validateMobile(settlementData.customerMobile);
      if (mobileValidationError) {
        toast.error(mobileValidationError);
        return;
      }
    }

    // Prepare settlement data with customer information and discounts
    const settlementPayload = {
      orderId: selectedOrder.id,
      paymentMode: settlementData.paymentMode,
      customerId: settlementData.customerId || undefined, // Only include if customer was selected from search
      customerName: settlementData.customerName,
      customerMobile: settlementData.customerMobile,
      customerEmail: settlementData.customerEmail || undefined,
      customerAddress: settlementData.customerAddress || undefined,
      // Include discount - only one type at a time
      discountPercent: (settlementData.discountPercent && settlementData.discountPercent > 0) ? settlementData.discountPercent : undefined,
      discountAmount: (settlementData.discountAmount && settlementData.discountAmount > 0) ? settlementData.discountAmount : undefined
    };

    // Filter out undefined values to keep payload clean
    const cleanPayload = Object.fromEntries(
      Object.entries(settlementPayload).filter(([_, value]) => value !== undefined && value !== '')
    );

    settleOrderAndPrintMutation.mutate(cleanPayload);
  };

  const totalOrderValue = Array.isArray(filteredOrders)
    ? filteredOrders.reduce((sum, order) => sum + order.total, 0)
    : 0;

  if (ordersLoading) {
    return (
      <div className="p-3 sm:p-4 md:p-6">
        <div className="space-y-4 sm:space-y-6">
          {/* Header Skeleton */}
          <SkeletonCard />

          {/* Summary Cards Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {[...Array(3)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>

          {/* Filters Skeleton */}
          <SkeletonCard />

          {/* Orders Grid Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            Orders Management
          </h1>
          <p className="text-xs sm:text-sm mt-1" style={{ color: currentTheme.colors.text.secondary }}>
            {selectedCustomer ? `Orders for ${selectedCustomer.name}` :
              dateRange.start && dateRange.end ? `Orders from ${new Date(dateRange.start).toLocaleDateString()} to ${new Date(dateRange.end).toLocaleDateString()}` :
                'Showing open orders only'}
          </p>
        </div>
        <button
          onClick={() => navigate('/place-order')}
          className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg transition-colors w-full sm:w-auto"
          style={{ backgroundColor: currentTheme.colors.primary[600] }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
        >
          <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          New Order
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        <div className="rounded-lg sm:rounded-xl border p-4 sm:p-6 shadow-sm" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg mr-3 sm:mr-4" style={{ backgroundColor: currentTheme.colors.primary[100] }}>
              <ShoppingCart className="h-6 w-6 sm:h-8 sm:w-8" style={{ color: currentTheme.colors.primary[600] }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl sm:text-3xl font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>{filteredOrders.length}</p>
              <p className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg sm:rounded-xl border p-4 sm:p-6 shadow-sm" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-green-100 rounded-lg mr-3 sm:mr-4">
              <IndianRupee className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl sm:text-3xl font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>
                ₹{totalOrderValue.toLocaleString()}
              </p>
              <p className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Value</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg sm:rounded-xl border p-4 sm:p-6 shadow-sm" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg mr-3 sm:mr-4">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl sm:text-3xl font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>
                {Array.isArray(filteredOrders) ? new Set(filteredOrders.map(o => o.tableId)).size : 0}
              </p>
              <p className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>Tables in Use</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg sm:rounded-xl border p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm" style={{
        backgroundColor: currentTheme.colors.background.card,
        borderColor: currentTheme.colors.border.primary
      }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Filter className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: currentTheme.colors.text.secondary }} />
            <span className="text-base sm:text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>Filters</span>
          </div>
          {(selectedCustomer || dateRange.start || dateRange.end || searchTerm) && (
            <button
              onClick={clearAllFilters}
              className="text-xs sm:text-sm px-3 py-1.5 sm:py-1 rounded-lg border transition-colors w-full sm:w-auto"
              style={{
                borderColor: currentTheme.colors.border.primary,
                color: currentTheme.colors.text.secondary,
                backgroundColor: currentTheme.colors.background.secondary
              }}
            >
              Clear All Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {/* Search Orders */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
              Search Orders
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5" style={{ color: currentTheme.colors.text.secondary }} />
              <input
                type="text"
                placeholder="Search by order ID or table..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                style={{
                  borderColor: currentTheme.colors.border.primary,
                  backgroundColor: currentTheme.colors.background.secondary,
                  color: currentTheme.colors.text.primary
                }}
              />
            </div>
          </div>

          {/* Search by Customer */}
          <div className="relative">
            <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
              Search by Customer
              {selectedCustomer && (
                <span className="ml-2 text-xs px-2 py-0.5 sm:py-1 rounded-full" style={{
                  backgroundColor: currentTheme.colors.primary[100],
                  color: currentTheme.colors.primary[700]
                }}>
                  Selected
                </span>
              )}
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5" style={{ color: currentTheme.colors.text.secondary }} />
              <input
                type="text"
                placeholder="Type customer name or mobile..."
                value={customerFilter}
                onChange={(e) => {
                  setCustomerFilter(e.target.value);
                  if (!selectedCustomer && e.target.value) {
                    setShowCustomerDropdown(true);
                  }
                }}
                onFocus={() => {
                  if (!selectedCustomer && customerFilter) {
                    setShowCustomerDropdown(true);
                  }
                }}
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                style={{
                  borderColor: selectedCustomer ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                  backgroundColor: currentTheme.colors.background.secondary,
                  color: currentTheme.colors.text.primary
                }}
              />
              {customerFilter && (
                <button
                  onClick={() => {
                    setCustomerFilter('');
                    setSelectedCustomer(null);
                    setShowCustomerDropdown(false);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded"
                  style={{ color: currentTheme.colors.text.secondary }}
                >
                  <X className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
              )}

              {/* Customer Dropdown */}
              {showCustomerDropdown && customers && Array.isArray(customers) && customers.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto" style={{
                  backgroundColor: currentTheme.colors.background.card,
                  borderColor: currentTheme.colors.border.primary
                }}>
                  {customers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                      className="p-3 cursor-pointer border-b hover:bg-gray-50 last:border-b-0"
                      style={{
                        borderColor: currentTheme.colors.border.secondary
                      }}
                    >
                      <div className="font-medium" style={{ color: currentTheme.colors.text.primary }}>
                        {customer.name}
                      </div>
                      <div className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        {customer.mobile}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {customerFilter && customerFilter !== debouncedCustomerFilter && (
                <div className="absolute top-full left-0 right-0 mt-1 p-2 text-center border rounded-lg" style={{
                  backgroundColor: currentTheme.colors.background.card,
                  borderColor: currentTheme.colors.border.primary,
                  color: currentTheme.colors.text.secondary
                }}>
                  Searching...
                </div>
              )}
            </div>
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
              Date Range
              {dateRange.start && dateRange.end && (
                <span className="ml-2 text-xs px-2 py-0.5 sm:py-1 rounded-full" style={{
                  backgroundColor: currentTheme.colors.primary[100],
                  color: currentTheme.colors.primary[700]
                }}>
                  Applied
                </span>
              )}
            </label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="flex-1 px-2 sm:px-3 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                style={{
                  borderColor: (dateRange.start && dateRange.end) ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                  backgroundColor: currentTheme.colors.background.secondary,
                  color: currentTheme.colors.text.primary
                }}
              />
              <span className="text-xs sm:text-sm text-center" style={{ color: currentTheme.colors.text.secondary }}>to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="flex-1 px-2 sm:px-3 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                style={{
                  borderColor: (dateRange.start && dateRange.end) ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                  backgroundColor: currentTheme.colors.background.secondary,
                  color: currentTheme.colors.text.primary
                }}
              />
            </div>
            {dateRange.start && dateRange.end && (
              <p className="text-xs mt-1" style={{ color: currentTheme.colors.text.secondary }}>
                Showing orders from {new Date(dateRange.start).toLocaleDateString()} to {new Date(dateRange.end).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      {filteredOrders.length === 0 ? (
        <EmptyState
          icon={<ShoppingCart className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No Orders Found"
          description={searchTerm || customerFilter ? 'No orders match your search criteria.' : 'No open orders at the moment.'}
          action={
            !searchTerm && !customerFilter ? (
              <button
                onClick={() => navigate('/place-order')}
                className="text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors inline-flex items-center font-semibold"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
              >
                <Plus className="h-5 w-5 mr-2" />
                Place New Order
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {Array.isArray(filteredOrders) && filteredOrders.map((order) => {
            return <OrderCard key={order.id} order={order} tableMap={tableMap} currentTheme={currentTheme} onViewDetails={handleViewDetails} onSettle={handleSettle} onEdit={handleEditOrder} onPrint={handlePrint} getTableName={getTableName} isPrinting={printingOrderId === order.id} />;
          })}
        </div>
      )}

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
          <div className="rounded-lg sm:rounded-xl max-w-sm w-full max-h-[90vh] overflow-y-auto" style={{
            backgroundColor: currentTheme.colors.background.card
          }}>
            <div className="p-4 sm:p-6 border-b sticky top-0 z-10" style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.card }}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-2xl font-bold" style={{ color: currentTheme.colors.text.primary }}>Order Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-1.5 sm:p-2 rounded-lg transition-colors"
                  style={{ backgroundColor: currentTheme.colors.background.secondary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: currentTheme.colors.text.primary }} />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {/* Order Info */}
              <div className="grid grid-cols-1 sm:grid-cols-1 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <h3 className="font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>Order Information</h3>
                  <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Order No: {selectedOrder.orderNo}</p>
                  <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Table: {getTableName(selectedOrder.tableId)}</p>
                  <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Time: {format(new Date(selectedOrder.createdAt), 'MMM dd, yyyy HH:mm')}</p>
                  <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Status: {selectedOrder.status}</p>
                </div>
                {selectedOrder.customerId && customerMap[selectedOrder.customerId] && (
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>Customer Information</h3>
                    <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Name: {customerMap[selectedOrder.customerId].name}</p>
                    <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Mobile: {customerMap[selectedOrder.customerId].mobile}</p>
                    {customerMap[selectedOrder.customerId].email && (
                      <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Email: {customerMap[selectedOrder.customerId].email}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Items */}
              <div className="mb-6">
                <h3 className="font-semibold mb-4" style={{ color: currentTheme.colors.text.primary }}>Order Items</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-4 rounded-lg" style={{
                      backgroundColor: currentTheme.colors.background.secondary
                    }}>
                      <div className="flex-1">
                        <h4 className="font-medium" style={{ color: currentTheme.colors.text.primary }}>{item.name}</h4>
                        <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>₹{item.unitPrice} each</p>
                        {item.specialNotes && (
                          <p className="text-sm mt-1" style={{ color: currentTheme.colors.primary[600] }}>Note: {item.specialNotes}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Qty: {item.quantity}</p>
                        <p className="font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                          ₹{(item.unitPrice * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Notes */}
              {selectedOrder.notes && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>Order Notes</h3>
                  <p className="p-3 rounded-lg" style={{
                    color: currentTheme.colors.text.primary,
                    backgroundColor: currentTheme.colors.background.secondary
                  }}>{selectedOrder.notes}</p>
                </div>
              )}

              {/* Total Calculation */}
              <div className="border-t pt-4" style={{ borderColor: currentTheme.colors.border.primary }}>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span style={{ color: currentTheme.colors.text.secondary }}>Subtotal:</span>
                    <span style={{ color: currentTheme.colors.text.primary }}>₹{selectedOrder.subTotal.toLocaleString()}</span>
                  </div>
                  {isGstApplicable && selectedOrder.totalGstAmount && selectedOrder.totalGstAmount > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span style={{ color: currentTheme.colors.text.secondary }}>CGST ({(selectedOrder.totalGstAmount / selectedOrder.subTotal * 50).toFixed(1)}%):</span>
                        <span style={{ color: currentTheme.colors.text.primary }}>₹{(selectedOrder.totalGstAmount / 2).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: currentTheme.colors.text.secondary }}>SGST ({(selectedOrder.totalGstAmount / selectedOrder.subTotal * 50).toFixed(1)}%):</span>
                        <span style={{ color: currentTheme.colors.text.primary }}>₹{(selectedOrder.totalGstAmount / 2).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  {selectedOrder.discountAmount && selectedOrder.discountAmount > 0 && (
                    <div className="flex justify-between">
                      <span style={{ color: currentTheme.colors.text.secondary }}>Discount:</span>
                      <span className="text-red-600">-₹{selectedOrder.discountAmount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold border-t pt-2" style={{ borderColor: currentTheme.colors.border.primary }}>
                    <span style={{ color: currentTheme.colors.text.primary }}>Total:</span>
                    <span className="text-green-600">₹{selectedOrder.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settlement Modal */}
      {showSettleModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
          <div
            className="rounded-lg sm:rounded-xl max-w-4xl w-full h-[95vh] sm:h-[90vh] md:h-[85vh] overflow-hidden flex flex-col"
            style={{
              backgroundColor: currentTheme.colors.background.card
            }}
          >
            {/* Fixed Header */}
            <div className="p-3 sm:p-4 md:p-6 border-b flex-shrink-0" style={{ borderColor: currentTheme.colors.border.primary }}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold" style={{ color: currentTheme.colors.text.primary }}>Settle Order</h2>
                <button
                  onClick={() => setShowSettleModal(false)}
                  className="p-1.5 sm:p-2 rounded-lg transition-colors"
                  style={{ backgroundColor: currentTheme.colors.background.secondary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: currentTheme.colors.text.primary }} />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">

                  {/* Left Column - Order Summary & Payment */}
                  <div className="space-y-4 sm:space-y-6">
                    {/* Order Summary */}
                    <div className="rounded-lg p-3 sm:p-4" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                      <div className="flex justify-between items-center mb-2 gap-2">
                        <span className="font-medium text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                          {selectedOrder.orderNo}
                        </span>
                        <span className="text-xl sm:text-2xl font-bold"
                          style={{ color: currentTheme.colors.primary[600] }}>
                          ₹{selectedOrder.total.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        Table: {getTableName(selectedOrder.tableId)} • {selectedOrder.items.length} items
                      </p>
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                        <div className="flex justify-between text-sm">
                          <span style={{ color: currentTheme.colors.text.secondary }}>Subtotal:</span>
                          <span style={{ color: currentTheme.colors.text.primary }}>₹{selectedOrder.subTotal.toLocaleString()}</span>
                        </div>
                        {isGstApplicable && selectedOrder.totalGstAmount && selectedOrder.totalGstAmount > 0 && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span style={{ color: currentTheme.colors.text.secondary }}>CGST ({(selectedOrder.totalGstAmount / selectedOrder.subTotal * 50).toFixed(1)}%):</span>
                              <span style={{ color: currentTheme.colors.text.primary }}>₹{(selectedOrder.totalGstAmount / 2).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span style={{ color: currentTheme.colors.text.secondary }}>SGST ({(selectedOrder.totalGstAmount / selectedOrder.subTotal * 50).toFixed(1)}%):</span>
                              <span style={{ color: currentTheme.colors.text.primary }}>₹{(selectedOrder.totalGstAmount / 2).toFixed(2)}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between text-sm font-semibold">
                          <span style={{ color: currentTheme.colors.text.primary }}>Total:</span>
                          <span style={{ color: currentTheme.colors.text.primary }}>₹{selectedOrder.total.toLocaleString()}</span>
                        </div>
                        {((settlementData.discountPercent && settlementData.discountPercent > 0) || (settlementData.discountAmount && settlementData.discountAmount > 0)) && (
                          <>
                            {settlementData.discountPercent && settlementData.discountPercent > 0 && (
                              <div className="flex justify-between text-sm mt-2 pt-2 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                                <span style={{ color: currentTheme.colors.text.secondary }}>Discount ({settlementData.discountPercent}%):</span>
                                <span className="text-red-600">-₹{(settlementData.discountPercent * selectedOrder.total / 100).toLocaleString()}</span>
                              </div>
                            )}
                            {settlementData.discountAmount && settlementData.discountAmount > 0 && (
                              <div className="flex justify-between text-sm">
                                <span style={{ color: currentTheme.colors.text.secondary }}>Discount Amount:</span>
                                <span className="text-red-600">-₹{settlementData.discountAmount.toLocaleString()}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2" style={{ borderColor: currentTheme.colors.border.primary }}>
                              <span style={{ color: currentTheme.colors.text.primary }}>Final Total:</span>
                              <span className="text-green-600">₹{Math.max(0, selectedOrder.total - ((settlementData.discountPercent || 0) * selectedOrder.total / 100) - (settlementData.discountAmount || 0)).toLocaleString()}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Discount Section */}
                    <div className="p-3 sm:p-4 rounded-lg border-2 border-dashed" style={{ borderColor: currentTheme.colors.border.primary }}>
                      <h3 className="font-semibold mb-3 sm:mb-4 flex items-center text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                        <Percent className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                        Apply Discount (Optional)
                      </h3>

                      {/* Discount Type Selection */}
                      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                        <button
                          onClick={() => {
                            setDiscountType('percent');
                            setSettlementData(prev => ({ ...prev, discountAmount: null }));
                          }}
                          className={`p-2 sm:p-3 rounded-lg border-2 transition-all flex items-center justify-center space-x-1 sm:space-x-2 ${discountType === 'percent' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'hover:border-gray-300'
                            }`}
                          style={{
                            borderColor: discountType === 'percent' ? currentTheme.colors.primary[500] : currentTheme.colors.border.primary,
                            backgroundColor: discountType === 'percent' ? currentTheme.colors.primary[50] : currentTheme.colors.background.secondary,
                            color: discountType === 'percent' ? currentTheme.colors.primary[700] : currentTheme.colors.text.primary
                          }}
                        >
                          <Percent className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="font-medium text-xs sm:text-sm">Discount %</span>
                        </button>
                        <button
                          onClick={() => {
                            setDiscountType('amount');
                            setSettlementData(prev => ({ ...prev, discountPercent: null }));
                          }}
                          className={`p-2 sm:p-3 rounded-lg border-2 transition-all flex items-center justify-center space-x-1 sm:space-x-2 ${discountType === 'amount' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'hover:border-gray-300'
                            }`}
                          style={{
                            borderColor: discountType === 'amount' ? currentTheme.colors.primary[500] : currentTheme.colors.border.primary,
                            backgroundColor: discountType === 'amount' ? currentTheme.colors.primary[50] : currentTheme.colors.background.secondary,
                            color: discountType === 'amount' ? currentTheme.colors.primary[700] : currentTheme.colors.text.primary
                          }}
                        >
                          <IndianRupee className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="font-medium text-xs sm:text-sm">Discount ₹</span>
                        </button>
                      </div>

                      {/* Single Discount Input Field */}
                      {discountType && (
                        <div>
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            {discountType === 'percent' ? 'Discount Percentage' : 'Discount Amount (₹)'}
                          </label>
                          <input
                            type="number"
                            value={discountType === 'percent' ? (settlementData.discountPercent || '') : (settlementData.discountAmount || '')}
                            onChange={(e) => {
                              const value = e.target.value ? parseFloat(e.target.value) : null;
                              if (discountType === 'percent') {
                                setSettlementData(prev => ({ ...prev, discountPercent: value }));
                              } else {
                                setSettlementData(prev => ({ ...prev, discountAmount: value }));
                              }
                            }}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg text-sm"
                            style={{
                              borderColor: currentTheme.colors.border.primary,
                              backgroundColor: currentTheme.colors.background.secondary,
                              color: currentTheme.colors.text.primary
                            }}
                            placeholder={discountType === 'percent' ? 'Enter percentage (0-100)' : 'Enter amount'}
                            min="0"
                            max={discountType === 'percent' ? "100" : undefined}
                          />
                        </div>
                      )}
                    </div>

                    {/* Payment Mode Selection */}
                    <div>
                      <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>Payment Method</h3>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        {(['CASH', 'UPI', 'CARD', 'DUE'] as PaymentMode[]).map((mode) => (
                          <button
                            key={mode}
                            onClick={() => setSettlementData(prev => ({ ...prev, paymentMode: mode }))}
                            className={clsx(
                              'p-2.5 sm:p-3 md:p-4 rounded-lg border-2 transition-all flex items-center justify-center space-x-1 sm:space-x-2',
                              settlementData.paymentMode === mode
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'hover:border-gray-300'
                            )}
                            style={{
                              borderColor: settlementData.paymentMode === mode ? currentTheme.colors.primary[500] : currentTheme.colors.border.primary,
                              backgroundColor: settlementData.paymentMode === mode ? currentTheme.colors.primary[50] : currentTheme.colors.background.secondary,
                              color: settlementData.paymentMode === mode ? currentTheme.colors.primary[700] : currentTheme.colors.text.primary
                            }}
                          >
                            {mode === 'CASH' && <Banknote className="h-4 w-4 sm:h-5 sm:w-5" />}
                            {mode === 'UPI' && <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />}
                            {mode === 'CARD' && <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />}
                            {mode === 'DUE' && <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />}
                            <span className="font-medium text-xs sm:text-sm">{mode}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Customer Information */}
                  <div className="mt-6 lg:mt-0">
                    <div className="mb-4 sm:mb-6">
                      <h3 className="font-semibold mb-3 sm:mb-4 flex flex-wrap items-center gap-2 text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                        <Users className="h-4 w-4 sm:h-5 sm:w-5 mr-1" style={{ color: currentTheme.colors.primary[600] }} />
                        Customer Information
                        {settlementData.paymentMode === 'DUE' && (
                          <span className="text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 bg-red-100 text-red-700 rounded-full">
                            Required for DUE
                          </span>
                        )}
                      </h3>

                      <div className="space-y-3 sm:space-y-4">
                        {/* Mobile Number with Search */}
                        <div className="relative">
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            Mobile Number *
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5" style={{ color: currentTheme.colors.text.secondary }} />
                            <input
                              type="tel"
                              value={settlementData.customerMobile}
                              onChange={(e) => handleMobileChange(e.target.value)}
                              className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm ${mobileError ? 'border-red-500' : ''
                                }`}
                              style={{
                                borderColor: mobileError ? '#ef4444' : (selectedCustomerFromSearch ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary),
                                backgroundColor: currentTheme.colors.background.secondary,
                                color: currentTheme.colors.text.primary
                              }}
                              placeholder="Enter 10-digit mobile number"
                              maxLength={10}
                            />
                            {selectedCustomerFromSearch && (
                              <button
                                onClick={handleClearCustomerSelection}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded"
                                style={{ color: currentTheme.colors.text.secondary }}
                                title="Clear selection and enter manually"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          {mobileError && (
                            <p className="text-red-500 text-xs mt-1">{mobileError}</p>
                          )}

                          {/* Customer Search Dropdown */}
                          {showCustomerSearchDropdown && customerSearchResults && customerSearchResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto" style={{
                              backgroundColor: currentTheme.colors.background.card,
                              borderColor: currentTheme.colors.border.primary
                            }}>
                              {customerSearchResults.map((customer: any) => (
                                <div
                                  key={customer.id}
                                  onClick={() => handleCustomerSelectFromSearch(customer)}
                                  className="p-3 cursor-pointer border-b hover:bg-gray-50 last:border-b-0"
                                  style={{ borderColor: currentTheme.colors.border.secondary }}
                                >
                                  <div className="font-medium" style={{ color: currentTheme.colors.text.primary }}>
                                    {customer.name}
                                  </div>
                                  <div className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                                    {customer.mobile} {customer.email && `• ${customer.email}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Customer Name */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            Customer Name *
                          </label>
                          <input
                            type="text"
                            value={settlementData.customerName}
                            onChange={(e) => setSettlementData(prev => ({ ...prev, customerName: e.target.value }))}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                            style={{
                              borderColor: selectedCustomerFromSearch ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                              backgroundColor: currentTheme.colors.background.secondary,
                              color: currentTheme.colors.text.primary
                            }}
                            placeholder="Enter customer name"
                            disabled={!!selectedCustomerFromSearch}
                          />
                        </div>

                        {/* Email Address (Optional) */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            Email Address <span className="text-xs text-gray-500">(Optional)</span>
                          </label>
                          <input
                            type="email"
                            value={settlementData.customerEmail}
                            onChange={(e) => {
                              setSettlementData(prev => ({ ...prev, customerEmail: e.target.value }));
                              setEmailError(validateEmail(e.target.value));
                            }}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                            style={{
                              borderColor: emailError ? '#ef4444' : selectedCustomerFromSearch ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                              backgroundColor: currentTheme.colors.background.secondary,
                              color: currentTheme.colors.text.primary
                            }}
                            placeholder="Enter email address"
                            disabled={!!selectedCustomerFromSearch}
                          />
                          {emailError && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{emailError}</p>}
                        </div>

                        {/* Address (Optional) */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            Address <span className="text-xs text-gray-500">(Optional)</span>
                          </label>
                          <textarea
                            value={settlementData.customerAddress}
                            onChange={(e) => setSettlementData(prev => ({ ...prev, customerAddress: e.target.value }))}
                            rows={2}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 resize-none text-sm"
                            style={{
                              borderColor: selectedCustomerFromSearch ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                              backgroundColor: currentTheme.colors.background.secondary,
                              color: currentTheme.colors.text.primary
                            }}
                            placeholder="Enter customer address"
                            disabled={!!selectedCustomerFromSearch}
                          />
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Footer with Action Buttons */}
            <div className="p-3 sm:p-4 md:p-6 border-t flex-shrink-0" style={{ borderColor: currentTheme.colors.border.primary }}>
              <div className="flex flex-wrap justify-end gap-3">
                <button
                  onClick={() => setShowSettleModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSettlement}
                  disabled={settleOrderMutation.isLoading || settleOrderAndPrintMutation.isLoading}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  style={{
                    backgroundColor: currentTheme.colors.primary[600]
                  }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {settleOrderMutation.isLoading ? (
                    <ButtonLoader text="Settling..." />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Settle Order
                    </>
                  )}
                </button>
                <button
                  onClick={confirmSettlementAndPrint}
                  disabled={settleOrderMutation.isLoading || settleOrderAndPrintMutation.isLoading}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  style={{
                    backgroundColor: currentTheme.colors.primary[600]
                  }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {settleOrderAndPrintMutation.isLoading ? (
                    <ButtonLoader text={willUseShare() ? "Settling & Sharing..." : "Settling & Printing..."} />
                  ) : willUseShare() ? (
                    <>
                      <Share2 className="h-4 w-4 mr-2" />
                      Settle & Share
                    </>
                  ) : (
                    <>
                      <Printer className="h-4 w-4 mr-2" />
                      Settle & Print
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditModal && selectedOrder && editOrderData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
          <div
            className="rounded-lg sm:rounded-xl max-w-4xl w-full h-[95vh] sm:h-[90vh] overflow-hidden flex flex-col"
            style={{
              backgroundColor: currentTheme.colors.background.card
            }}
          >
            {/* Fixed Header */}
            <div className="p-3 sm:p-4 md:p-6 border-b flex-shrink-0" style={{ borderColor: currentTheme.colors.border.primary }}>
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg md:text-xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
                  <Edit3 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                  <span className="truncate">Edit Order {selectedOrder.orderNo}</span>
                </h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0 ml-2"
                  style={{ backgroundColor: currentTheme.colors.background.secondary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: currentTheme.colors.text.primary }} />
                </button>
              </div>
              <p className="text-xs sm:text-sm mt-1 sm:mt-2" style={{ color: currentTheme.colors.text.secondary }}>
                Table: {getTableName(selectedOrder.tableId)} • {selectedOrder.status}
              </p>
            </div>

            {/* Content with Individual Scroll Areas */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-6">

                  {/* Left Column - Current Order Items */}
                  <div className="flex flex-col">
                    <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
                      <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                      Current Order Items
                    </h3>

                    {/* Scrollable Left Content */}
                    <div className="space-y-2 sm:space-y-3">
                      {editOrderData.items.map((item, index: number) => (
                        <div key={`${item.itemId}-${index}`} className="p-3 sm:p-4 rounded-lg border" style={{
                          backgroundColor: currentTheme.colors.background.secondary,
                          borderColor: currentTheme.colors.border.primary
                        }}>
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <h4 className="font-medium text-sm sm:text-base flex-1 min-w-0" style={{ color: currentTheme.colors.text.primary }}>{item.name}</h4>
                            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                              <button
                                onClick={() => updateItemQuantity(item.itemId, item.quantity - 1)}
                                className="p-1 rounded-lg transition-colors"
                                style={{ backgroundColor: currentTheme.colors.background.hover }}
                              >
                                <Minus className="h-3 w-3 sm:h-4 sm:w-4" style={{ color: currentTheme.colors.text.primary }} />
                              </button>
                              <span className="font-medium px-2 sm:px-3 py-0.5 sm:py-1 rounded text-sm" style={{
                                backgroundColor: currentTheme.colors.primary[100],
                                color: currentTheme.colors.primary[700]
                              }}>
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateItemQuantity(item.itemId, item.quantity + 1)}
                                className="p-1 rounded-lg transition-colors"
                                style={{ backgroundColor: currentTheme.colors.background.hover }}
                              >
                                <Plus className="h-3 w-3 sm:h-4 sm:w-4" style={{ color: currentTheme.colors.text.primary }} />
                              </button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
                            <span style={{ color: currentTheme.colors.text.secondary }}>₹{item.unitPrice} each</span>
                            <span className="font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                              ₹{(item.unitPrice * item.quantity).toLocaleString()}
                            </span>
                          </div>

                          <input
                            type="text"
                            value={item.specialNotes}
                            onChange={(e) => updateItemNotes(item.itemId, e.target.value)}
                            placeholder="Special notes (optional)"
                            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg text-xs sm:text-sm"
                            style={{
                              borderColor: currentTheme.colors.border.primary,
                              backgroundColor: currentTheme.colors.background.card,
                              color: currentTheme.colors.text.primary
                            }}
                          />
                        </div>
                      ))}

                      {/* Order Notes and Discounts - Inside Scrollable Area */}
                      <div className="mt-4 sm:mt-6 p-3 sm:p-4 md:p-6 rounded-lg border-2 border-dashed" style={{ borderColor: currentTheme.colors.border.primary }}>
                        <h4 className="text-sm sm:text-base font-semibold mb-3 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
                          <Percent className="h-3 w-3 sm:h-4 sm:w-4 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                          Order Notes & Discounts
                        </h4>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                              Order Notes
                            </label>
                            <textarea
                              value={editOrderData.notes}
                              onChange={(e) => setEditOrderData((prev: EditOrderData | null) => prev ? { ...prev, notes: e.target.value } : null)}
                              rows={2}
                              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg resize-none text-xs sm:text-sm"
                              style={{
                                borderColor: currentTheme.colors.border.primary,
                                backgroundColor: currentTheme.colors.background.secondary,
                                color: currentTheme.colors.text.primary
                              }}
                              placeholder="Add order notes..."
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-2 sm:gap-3">
                            <div>
                              <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                                Discount %
                              </label>
                              <input
                                type="number"
                                value={editOrderData.discountPercent}
                                onChange={(e) => setEditOrderData((prev: EditOrderData | null) => prev ? {
                                  ...prev,
                                  discountPercent: parseFloat(e.target.value) || 0
                                } : null)}
                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg text-xs sm:text-sm"
                                style={{
                                  borderColor: currentTheme.colors.border.primary,
                                  backgroundColor: currentTheme.colors.background.secondary,
                                  color: currentTheme.colors.text.primary
                                }}
                                placeholder="0"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                                Discount Amount (₹)
                              </label>
                              <input
                                type="number"
                                value={editOrderData.discountAmount}
                                onChange={(e) => setEditOrderData((prev: EditOrderData | null) => prev ? {
                                  ...prev,
                                  discountAmount: parseFloat(e.target.value) || 0
                                } : null)}
                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg text-xs sm:text-sm"
                                style={{
                                  borderColor: currentTheme.colors.border.primary,
                                  backgroundColor: currentTheme.colors.background.secondary,
                                  color: currentTheme.colors.text.primary
                                }}
                                placeholder="0"
                                min="0"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Add Items */}
                  <div className="flex flex-col mt-6 lg:mt-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
                      <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                      Add Items
                    </h3>

                    {/* Search Items */}
                    <div className="mb-3 sm:mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5" style={{ color: currentTheme.colors.text.secondary }} />
                        <input
                          type="text"
                          placeholder="Search menu items..."
                          value={itemSearchTerm}
                          onChange={(e) => setItemSearchTerm(e.target.value)}
                          className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                          style={{
                            borderColor: currentTheme.colors.border.primary,
                            backgroundColor: currentTheme.colors.background.secondary,
                            color: currentTheme.colors.text.primary
                          }}
                        />
                      </div>
                      <p className="text-xs sm:text-sm mt-2" style={{ color: currentTheme.colors.text.secondary }}>
                        {itemSearchTerm ?
                          (searchItemsLoading ? 'Searching...' :
                            itemSearchTerm !== debouncedItemSearchTerm ? 'Typing...' :
                              `Showing results for "${itemSearchTerm}"`) :
                          (searchItemsLoading ? 'Loading items...' : 'Showing all available items')
                        }
                      </p>
                    </div>

                    {/* Scrollable Add Items List */}
                    <div className="space-y-2 sm:space-y-3">
                      {searchItemsLoading ? (
                        <SkeletonList items={5} />
                      ) : searchItems && searchItems.length > 0 ? (
                        <div className="space-y-2 sm:space-y-3">
                          {searchItems.map((item: any) => {
                            const isUnavailable = !item.available;

                            return (
                              <div key={item.id}
                                className={`p-3 sm:p-4 rounded-lg border transition-all ${!isUnavailable ? 'cursor-pointer hover:shadow-md' : 'cursor-not-allowed'}`}
                                style={{
                                  backgroundColor: isUnavailable ? currentTheme.colors.background.hover : currentTheme.colors.background.secondary,
                                  borderColor: isUnavailable ? '#9ca3af' : currentTheme.colors.border.primary,
                                  opacity: isUnavailable ? 0.6 : 1
                                }}
                                onClick={() => !isUnavailable && addItemToEdit(item)}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <h4
                                        className="font-medium text-sm sm:text-base truncate"
                                        style={{ color: isUnavailable ? '#9ca3af' : currentTheme.colors.text.primary }}
                                      >
                                        {item.name}
                                      </h4>
                                      {item.vegType === 'VEG' && <span className="text-green-600 text-xs sm:text-sm flex-shrink-0">🟢</span>}
                                      {item.vegType === 'NON_VEG' && <span className="text-red-600 text-xs sm:text-sm flex-shrink-0">🔴</span>}
                                    </div>
                                    <p
                                      className="text-xs sm:text-sm"
                                      style={{ color: isUnavailable ? '#9ca3af' : currentTheme.colors.text.secondary }}
                                    >
                                      ₹{item.price}
                                    </p>
                                    {item.description && (
                                      <p className="text-xs mt-1 line-clamp-2" style={{ color: currentTheme.colors.text.secondary }}>
                                        {item.description}
                                      </p>
                                    )}
                                    {isUnavailable && (
                                      <p className="text-xs font-medium mt-1 text-red-600">
                                        ❌ Not available
                                      </p>
                                    )}
                                  </div>
                                  {isUnavailable && (
                                    <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-100 text-red-800 text-[10px] sm:text-xs font-medium rounded-full border border-red-200 whitespace-nowrap flex-shrink-0">
                                      N/A
                                    </span>
                                  )}
                                  {!isUnavailable && (
                                    <button
                                      disabled={isUnavailable}
                                      className={`p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0 ${isUnavailable ? 'cursor-not-allowed' : ''}`}
                                      style={{
                                        backgroundColor: isUnavailable ? currentTheme.colors.background.secondary : currentTheme.colors.primary[100]
                                      }}
                                    >
                                      <Plus
                                        className="h-4 w-4 sm:h-5 sm:w-5"
                                        style={{
                                          color: isUnavailable ? '#9ca3af' : currentTheme.colors.primary[600]
                                        }}
                                      />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-40">
                          <div className="text-center">
                            <Search className="h-16 w-16 mx-auto mb-4" style={{ color: currentTheme.colors.text.secondary }} />
                            <h3 className="text-lg font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                              {itemSearchTerm ? 'No Items Found' : 'No Items Available'}
                            </h3>
                            <p style={{ color: currentTheme.colors.text.secondary }}>
                              {itemSearchTerm ?
                                `No items match your search for "${itemSearchTerm}"` :
                                'No menu items are currently available'
                              }
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Footer with Action Buttons */}
            <div className="p-3 sm:p-4 md:p-6 border-t flex-shrink-0" style={{ borderColor: currentTheme.colors.border.primary }}>
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
                <div className="text-sm sm:text-base md:text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                  Total Items: {editOrderData.items.reduce((sum: number, item) => sum + item.quantity, 0)}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 py-2 px-3 rounded-lg transition-colors flex items-center justify-center font-medium border text-sm"
                    style={{
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary,
                      borderColor: currentTheme.colors.border.primary
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmOrderUpdate}
                    disabled={updateOrderMutation.isLoading || editOrderData.items.length === 0}
                    className="px-4 sm:px-6 py-2 sm:py-3 text-white rounded-lg transition-colors flex items-center justify-center font-medium disabled:opacity-50 text-sm sm:text-base"
                    style={{
                      backgroundColor: currentTheme.colors.primary[600]
                    }}
                    onMouseEnter={(e) => !updateOrderMutation.isLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                    onMouseLeave={(e) => !updateOrderMutation.isLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                  >
                    {updateOrderMutation.isLoading ? (
                      <ButtonLoader text="Updating..." />
                    ) : (
                      <>
                        <Save className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        Update Order
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Order Card Component
const OrderCard: React.FC<{
  order: Order;
  tableMap: Record<string, string>;
  currentTheme: any;
  onViewDetails: (order: Order) => void;
  onSettle: (order: Order, customer?: any) => void;
  onEdit: (order: Order) => void;
  onPrint: (order: Order) => void;
  getTableName: (id?: string | number) => string;
  isPrinting?: boolean;
}> = ({ order, tableMap, currentTheme, onViewDetails, onSettle, onEdit, onPrint, getTableName, isPrinting = false }) => {

  // Fetch individual customer data if customerId exists
  const { data: customer } = useQuery(
    ['customer', order.customerId],
    () => customerService.get(order.customerId!),
    {
      enabled: !!order.customerId,
    }
  );

  return (
    <div className="rounded-xl border shadow-sm hover:shadow-md transition-all" style={{
      backgroundColor: currentTheme.colors.background.card,
      borderColor: currentTheme.colors.border.primary
    }}>
      <div className="p-6">
        {/* Customer Info Row - Full width at top */}
        {customer ? (
          <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: currentTheme.colors.primary[50] }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                <div>
                  <p className="font-medium" style={{ color: currentTheme.colors.primary[700] }}>
                    {customer.name}
                  </p>
                  <p className="text-sm" style={{ color: currentTheme.colors.primary[600] }}>
                    {customer.mobile}
                    {customer.email && ` • ${customer.email}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center px-3 py-1 rounded-full text-sm font-medium" style={{
                backgroundColor: order.status === 'OPEN' ? currentTheme.colors.primary[150] : order.status === 'SETTLED' ? currentTheme.colors.primary[50] : currentTheme.colors.primary[150],
                color: order.status === 'OPEN' ? currentTheme.colors.primary[600] : order.status === 'SETTLED' ? currentTheme.colors.primary[700] : currentTheme.colors.primary[500]
              }}>
                <Clock3 className="h-4 w-4 mr-1" />
                {order.status}
              </div>
            </div>
          </div>
        ) : order.customerId ? (
          <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
                <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                  Loading customer details...
                </p>
              </div>
              <div className="flex items-center px-3 py-1 rounded-full text-sm font-medium" style={{
                backgroundColor: order.status === 'OPEN' ? currentTheme.colors.primary[150] : order.status === 'SETTLED' ? currentTheme.colors.primary[50] : currentTheme.colors.primary[150],
                color: order.status === 'OPEN' ? currentTheme.colors.primary[600] : order.status === 'SETTLED' ? currentTheme.colors.primary[700] : currentTheme.colors.primary[500]
              }}>
                <Clock3 className="h-4 w-4 mr-1" />
                {order.status}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
                <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                  Walk-in Customer
                </p>
              </div>
              <div className="flex items-center px-3 py-1 rounded-full text-sm font-medium" style={{
                backgroundColor: order.status === 'OPEN' ? currentTheme.colors.primary[150] : order.status === 'SETTLED' ? currentTheme.colors.primary[50] : currentTheme.colors.primary[150],
                color: order.status === 'OPEN' ? currentTheme.colors.primary[600] : order.status === 'SETTLED' ? currentTheme.colors.primary[700] : currentTheme.colors.primary[500]
              }}>
                <Clock3 className="h-4 w-4 mr-1" />
                {order.status}
              </div>
            </div>
          </div>
        )}

        {/* Order Header */}
        <div className="mb-4">
          <h3 className="text-xl font-bold" style={{ color: currentTheme.colors.text.primary }}>
            Table #{getTableName(order.tableId)}
          </h3>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
            {order.orderNo} • {format(new Date(order.createdAt), 'MMM dd, HH:mm')}
          </p>
        </div>

        {/* Order Items Summary */}
        <div className="space-y-2 mb-4">
          {order.items.slice(0, 3).map((item: any, index: number) => (
            <div key={index} className="flex items-center justify-between py-1 border-b last:border-0" style={{ borderColor: currentTheme.colors.border.secondary }}>
              <div className="flex-1">
                <p className="font-medium text-sm" style={{ color: currentTheme.colors.text.primary }}>{item.name}</p>
                {item.specialNotes && (
                  <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>Note: {item.specialNotes}</p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>×{item.quantity}</span>
                <span className="font-medium text-sm" style={{ color: currentTheme.colors.text.primary }}>
                  ₹{(item.unitPrice * item.quantity).toLocaleString()}
                </span>
              </div>
            </div>
          ))}
          {order.items.length > 3 && (
            <p className="text-sm font-medium" style={{ color: currentTheme.colors.primary[600] }}>
              +{order.items.length - 3} more items
            </p>
          )}
        </div>

        {/* Order Total */}
        <div className="border-t pt-4 mb-4" style={{ borderColor: currentTheme.colors.border.primary }}>
          <div className="flex justify-between items-center text-lg font-bold">
            <span style={{ color: currentTheme.colors.text.primary }}>Total:</span>
            <span style={{ color: currentTheme.colors.primary[600] }}>₹{order.total.toLocaleString()}</span>
          </div>
          {order.discountAmount && order.discountAmount > 0 && (
            <>
              <p className="text-sm mt-1" style={{ color: currentTheme.colors.primary[600] }}>
                Before Discount: ₹{order.subTotal.toLocaleString()}
              </p>
              <p className="text-sm mt-1" style={{ color: currentTheme.colors.primary[600] }}>
                Discount applied: ₹{order.discountAmount.toLocaleString()}
              </p>
            </>
          )}
          {order.discountPercent && order.discountPercent > 0 && (
            <>
              <p className="text-sm mt-1" style={{ color: currentTheme.colors.primary[600] }}>
                Before Discount: ₹{order.subTotal.toLocaleString()}
              </p>
              <p className="text-sm mt-1" style={{ color: currentTheme.colors.primary[600] }}>
                Discount applied: {order.discountPercent.toLocaleString()}%
              </p>
            </>
          )}
        </div>

        {/* Action Buttons */}

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => onViewDetails(order)}
            className="py-2 px-2 sm:px-3 rounded-lg transition-colors flex items-center justify-center font-medium border text-xs sm:text-sm"
            style={{
              backgroundColor: currentTheme.colors.background.secondary,
              color: currentTheme.colors.text.primary,
              borderColor: currentTheme.colors.border.primary
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
          >
            <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            View
          </button>
          <button
            onClick={() => onPrint(order)}
            disabled={isPrinting}
            className="py-2 px-2 sm:px-3 rounded-lg transition-colors flex items-center justify-center font-medium text-xs sm:text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: currentTheme.colors.primary[600]
            }}
            onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
            onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
          >
            {isPrinting ? (
              <ButtonLoader text={willUseShare() ? 'Sharing...' : 'Printing...'} />
            ) : willUseShare() ? (
              <>
                <Share2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="truncate">{order.status === 'SETTLED' ? 'Share Bill' : 'Share KOT'}</span>
              </>
            ) : (
              <>
                <Printer className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="truncate">{order.status === 'SETTLED' ? 'Print Bill' : 'Print KOT'}</span>
              </>
            )}
          </button>
          {order.status === 'OPEN' && (
            <>
              <button
                onClick={() => onEdit(order)}
                className="py-2 px-2 sm:px-3 rounded-lg transition-colors flex items-center justify-center font-medium text-xs sm:text-sm text-white"
                style={{ backgroundColor: '#f59e0b' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
              >
                <Edit3 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => onSettle(order, customer)}
                className="text-white py-2 px-2 sm:px-3 rounded-lg transition-colors flex items-center justify-center font-medium text-xs sm:text-sm"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
              >
                <Receipt className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                Settle
              </button>
            </>
          )}

        </div>
      </div>
    </div>
  );
};